<template>
  <div class="dropzone-wrap">
    <div
      class="upload-zone"
      :class="{ 'upload-zone--dragover': isDragOver }"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleDrop"
      @click="$refs.fileInput.click()"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        accept=".pdf,.docx,.png,.jpg,.jpeg,.xml,.xls,.xlsx"
        style="display:none"
        @change="handleFileChange"
      />
      <div class="upload-zone__empty">
        <span class="empty__icon">📁</span>
        <p class="empty__title">Dokumente hier ablegen</p>
        <p class="empty__sub">oder klicken zum Durchsuchen (mehrere Dateien möglich)</p>
        <p class="empty__types">Unterstützt: PDF, DOCX, PNG, JPG, XML, XLS, XLSX</p>
      </div>
    </div>

    <div v-if="stagedFiles.length" class="staged-list">
      <div v-for="(item, i) in stagedFiles" :key="i" class="staged-item">
        <span class="staged-item__name">📄 {{ item.file.name }}</span>
        <select class="field__select staged-item__select" v-model="item.category">
          <option value="">Kategorie wählen…</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.label }}</option>
        </select>
        <button class="staged-item__remove" @click="removeFile(i)">✕</button>
      </div>
    </div>

    <div v-if="requireAllCategories && categories.length" class="coverage-list">
      <span
        v-for="c in categories"
        :key="c.id"
        class="coverage-chip"
        :class="{ 'coverage-chip--done': isCovered(c.id) }"
      >
        {{ isCovered(c.id) ? '✓' : '○' }} {{ c.label }}<template v-if="isOptional(c.id) && !isCovered(c.id)"> (optional)</template>
      </span>
    </div>

    <div v-if="errorMessage" class="upload-error"><span>⚠</span> {{ errorMessage }}</div>

    <button class="submit-btn" :disabled="!canSubmit || busy" @click="submit">
      {{ busy ? 'Wird hochgeladen…' : buttonLabel }}
    </button>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

// "Nicht identifizierte Kategorie" is a catch-all, not something every
// citizen will have a document for - it shouldn't block submission.
const OPTIONAL_CATEGORY_IDS = ['misc'];

export default {
  name: 'CategorizedUploadDropzone',
  props: {
    categories: { type: Array, required: true },
    busy: { type: Boolean, default: false },
    buttonLabel: { type: String, default: 'Hochladen' },
    errorMessage: { type: String, default: null },
    // false for "add more documents to an existing case" - there every
    // category doesn't need to be covered again, just each staged file
    // needs some category assigned.
    requireAllCategories: { type: Boolean, default: true },
  },
  emits: ['submit'],

  setup(props, { emit }) {
    const fileInput = ref(null);
    const stagedFiles = ref([]);
    const isDragOver = ref(false);

    const addFiles = (fileList) => {
      const items = Array.from(fileList).map((file) => ({ file, category: '' }));
      stagedFiles.value = [...stagedFiles.value, ...items];
    };

    const handleFileChange = (e) => {
      if (e.target.files?.length) addFiles(e.target.files);
      e.target.value = '';
    };

    const handleDrop = (e) => {
      isDragOver.value = false;
      if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
      stagedFiles.value = stagedFiles.value.filter((_, i) => i !== index);
    };

    const isCovered = (categoryId) => stagedFiles.value.some((item) => item.category === categoryId);
    const isOptional = (categoryId) => OPTIONAL_CATEGORY_IDS.includes((categoryId || '').toLowerCase());

    const canSubmit = computed(() => {
      if (!stagedFiles.value.length || !stagedFiles.value.every((item) => item.category)) return false;
      if (!props.requireAllCategories) return true;
      return props.categories.every((c) => isOptional(c.id) || isCovered(c.id));
    });

    const submit = () => {
      if (!canSubmit.value) return;
      const filesByType = {};
      stagedFiles.value.forEach((item) => {
        (filesByType[item.category] ||= []).push(item.file);
      });
      emit('submit', filesByType);
    };

    const clear = () => { stagedFiles.value = []; };

    return {
      fileInput, stagedFiles, isDragOver, canSubmit,
      handleFileChange, handleDrop, removeFile, isCovered, isOptional, submit, clear,
    };
  },
};
</script>

<style scoped src="../forms/form.css"></style>
<style scoped>
.dropzone-wrap { display: flex; flex-direction: column; gap: 12px; }

.upload-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #fafafa;
}
.upload-zone--dragover { border-color: #1e293b; background: #f8fafc; }

.upload-zone__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px;
  text-align: center;
}
.empty__icon { font-size: 32px; }
.empty__title { font-size: 14px; font-weight: 700; color: #374151; }
.empty__sub { font-size: 12px; color: #9ca3af; }
.empty__types {
  font-size: 10px;
  color: #d1d5db;
  background: #f3f4f6;
  padding: 3px 10px;
  border-radius: 20px;
  margin-top: 2px;
}

.staged-list { display: flex; flex-direction: column; gap: 6px; }
.staged-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #065f46;
}
.staged-item__name { flex: 1; }
.staged-item__select { flex: 0 0 220px; }
.staged-item__remove {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 13px;
}

.coverage-list { display: flex; flex-wrap: wrap; gap: 8px; }
.coverage-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}
.coverage-chip--done { background: #f0fdf4; color: #065f46; border-color: #bbf7d0; }

.upload-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #dc2626;
  font-weight: 500;
}

.submit-btn {
  align-self: flex-start;
  background: #003366;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.submit-btn:hover:not(:disabled) { opacity: 0.9; }
</style>
